import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getJBPActualsAndTargetsCategoriesCrud } from 'legacy/lut/services/partner360/common/helpers/getJBPActualsAndTargetsCategoriesCrud'
import { IJBPActualsAndTargetsCategory } from 'legacy/lut/services/partner360/common/models/jbpActualsAndTargetsCategories'
import { useEffect, useState } from 'react'

import Logger from 'core/utils/Logger'

interface IUseBusinessUnits {
  programType: ProgramTypes
  fYear: number
}

interface IBusinessUnitsState extends IUseBusinessUnits {
  businessUnits: IJBPActualsAndTargetsCategory[]
  status: 'loading' | 'loaded' | 'error'
}

const EMPTY_BUSINESS_UNITS: IJBPActualsAndTargetsCategory[] = []

export const useBusinessUnitsData = ({ programType, fYear }: IUseBusinessUnits) => {
  const [state, setState] = useState<IBusinessUnitsState>()

  useEffect(() => {
    let isActive = true
    setState({ programType, fYear, businessUnits: EMPTY_BUSINESS_UNITS, status: 'loading' })

    const loadBusinessUnits = async () => {
      try {
        const businessUnitCrud = getJBPActualsAndTargetsCategoriesCrud({ programType, fYear })
        const response = await businessUnitCrud.fetch()
        if (response.error) throw new Error(response.error)
        if (isActive) {
          setState({ programType, fYear, businessUnits: response.items, status: 'loaded' })
        }
      } catch (error) {
        if (isActive) {
          Logger.error('Error loading Key Initiative Business Units', error)
          setState({ programType, fYear, businessUnits: EMPTY_BUSINESS_UNITS, status: 'error' })
        }
      }
    }

    void loadBusinessUnits()

    return () => {
      // The CRUD can be shared by other consumers; ignore this response without aborting it.
      isActive = false
    }
  }, [programType, fYear])

  // Do not expose the previous dictionary even on the first render after a context change.
  if (state?.programType !== programType || state?.fYear !== fYear) {
    return { businessUnits: EMPTY_BUSINESS_UNITS, isLoading: true, isLoaded: false }
  }

  return {
    businessUnits: state.businessUnits,
    isLoading: state.status === 'loading',
    isLoaded: state.status === 'loaded',
  }
}

// Keep the array-only contract for existing tables and editors.
export const useBusinessUnits = (params: IUseBusinessUnits) =>
  useBusinessUnitsData(params).businessUnits
